import React, { useEffect, useRef, useState } from 'react'
import { Button, Card, Empty, Input, Popconfirm, Radio, Slider, Space, Switch, Tooltip, Typography, message } from 'antd'
import {
  AudioOutlined, AudioMutedOutlined, DeleteOutlined, PauseCircleOutlined,
  PlayCircleOutlined, PlusOutlined, ScissorOutlined, SoundOutlined,
} from '@ant-design/icons'
import useAudioRecorder from './useAudioRecorder'
import { formatTime, makeId } from './utils'

const { Text } = Typography

/**
 * Editor for a single recorded clip: trim in/out, split at the playhead,
 * mute/adjust the original audio, and optionally record a voiceover that
 * either replaces the original audio or is mixed in alongside it.
 *
 * Non-destructive: everything here is metadata (in/out points, volume,
 * mute, voiceover blob) stored on the clip object. The actual re-encode
 * only happens once, at export time.
 */
const ClipEditor = ({ clip, onChange, onSplit, onDelete }) => {
  const videoRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [playhead, setPlayhead] = useState(clip.inSec)

  const voiceRecorder = useAudioRecorder({
    onClipReady: (blob) => {
      onChange({ voiceoverBlob: blob, voiceoverUrl: URL.createObjectURL(blob), voiceoverMode: clip.voiceoverMode || 'replace' })
    },
    onError: () => message.error('Could not record voiceover — check microphone permission.'),
  })

  // Keep playback pinned inside the current trim range.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    const onTime = () => {
      setPlayhead(v.currentTime)
      if (v.currentTime >= clip.outSec) {
        v.pause()
        setPlaying(false)
        v.currentTime = clip.inSec
      }
    }
    v.addEventListener('timeupdate', onTime)
    return () => v.removeEventListener('timeupdate', onTime)
  }, [clip.inSec, clip.outSec])

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (playing) {
      v.pause()
      setPlaying(false)
    } else {
      if (v.currentTime < clip.inSec || v.currentTime >= clip.outSec) v.currentTime = clip.inSec
      v.play()
      setPlaying(true)
    }
  }

  const handleTrimChange = ([inSec, outSec]) => {
    onChange({ inSec, outSec })
    const v = videoRef.current
    if (v) v.currentTime = inSec
  }

  const handleSplit = () => {
    const at = videoRef.current?.currentTime ?? playhead
    if (at <= clip.inSec + 0.2 || at >= clip.outSec - 0.2) {
      message.info('Move the playhead further from the trim edges before splitting.')
      return
    }
    onSplit(at)
  }

  const trimmedLen = Math.max(0, clip.outSec - clip.inSec)

  const captions = clip.captions || []

  const updateCaption = (id, patch) => {
    onChange({ captions: captions.map((c) => (c.id === id ? { ...c, ...patch } : c)) })
  }

  const deleteCaption = (id) => {
    onChange({ captions: captions.filter((c) => c.id !== id) })
  }

  const addCaption = () => {
    const start = Math.min(Math.max(playhead, clip.inSec), Math.max(clip.inSec, clip.outSec - 0.5))
    const end = Math.min(start + 3, clip.outSec)
    const line = { id: makeId('cap'), start, end, text: '' }
    onChange({ captions: [...captions, line].sort((a, b) => a.start - b.start) })
  }

  const seekTo = (t) => {
    const v = videoRef.current
    if (v) v.currentTime = Math.min(Math.max(t, clip.inSec), clip.outSec)
  }

  return (
    <Card
      size='small'
      style={{ borderRadius: 10 }}
      title={<Text strong>Editing: {clip.name}</Text>}
      extra={
        <Popconfirm title='Delete this clip?' onConfirm={onDelete} okText='Delete' okButtonProps={{ danger: true }}>
          <Button size='small' danger icon={<DeleteOutlined />}>Delete Clip</Button>
        </Popconfirm>
      }
    >
      <div style={{ background: '#000', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
        <video
          ref={videoRef}
          src={clip.url}
          style={{ width: '100%', maxHeight: 360, display: 'block' }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />
      </div>

      <Space align='center' style={{ marginBottom: 4 }}>
        <Button shape='circle' icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />} onClick={togglePlay} />
        <Text type='secondary' style={{ fontSize: 12 }}>
          {formatTime(playhead)} / {formatTime(clip.durationSec)} · trimmed length: {formatTime(trimmedLen)}
        </Text>
        <Tooltip title='Split the clip into two, at the current playhead position'>
          <Button size='small' icon={<ScissorOutlined />} onClick={handleSplit}>Split Here</Button>
        </Tooltip>
      </Space>

      <div style={{ padding: '4px 6px 12px' }}>
        <Slider
          range
          min={0}
          max={clip.durationSec || 0.1}
          step={0.1}
          value={[clip.inSec, clip.outSec]}
          onChange={handleTrimChange}
          tooltip={{ formatter: (v) => formatTime(v) }}
        />
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 8 }}>
        {/* Original audio controls */}
        <div style={{ minWidth: 220 }}>
          <Space style={{ marginBottom: 6 }}>
            {clip.muted ? <AudioMutedOutlined /> : <AudioOutlined />}
            <Text strong style={{ fontSize: 13 }}>Original audio</Text>
            {clip.hasAudio !== false && (
              <Switch
                checked={!clip.muted}
                onChange={(checked) => onChange({ muted: !checked })}
                checkedChildren='On'
                unCheckedChildren='Muted'
              />
            )}
          </Space>
          {clip.hasAudio === false ? (
            <Text type='secondary' style={{ fontSize: 12 }}>
              This recording has no audio track (no system audio or microphone was captured).
            </Text>
          ) : !clip.muted && (
            <div style={{ paddingLeft: 2 }}>
              <Text type='secondary' style={{ fontSize: 12 }}>Volume — {Math.round((clip.volume ?? 1) * 100)}%</Text>
              <Slider min={0} max={2} step={0.05} value={clip.volume ?? 1} onChange={(v) => onChange({ volume: v })} />
            </div>
          )}
        </div>

        {/* Voiceover controls */}
        <div style={{ minWidth: 260, flex: 1 }}>
          <Space style={{ marginBottom: 6 }}>
            <SoundOutlined />
            <Text strong style={{ fontSize: 13 }}>Voiceover</Text>
          </Space>
          {!clip.voiceoverBlob ? (
            <div>
              {voiceRecorder.status === 'recording' ? (
                <Button danger icon={<AudioOutlined />} onClick={voiceRecorder.stop}>
                  Stop Recording ({formatTime(voiceRecorder.elapsedSec)})
                </Button>
              ) : (
                <Button icon={<AudioOutlined />} loading={voiceRecorder.status === 'requesting'} onClick={voiceRecorder.start}>
                  Record Voiceover
                </Button>
              )}
              <div style={{ fontSize: 12, color: '#98A2B3', marginTop: 4 }}>
                Record narration to replace or mix with this clip's audio.
              </div>
            </div>
          ) : (
            <div>
              <audio controls src={clip.voiceoverUrl} style={{ width: '100%', height: 32, marginBottom: 6 }} />
              <Space direction='vertical' style={{ width: '100%' }}>
                <Radio.Group
                  size='small'
                  value={clip.voiceoverMode || 'replace'}
                  onChange={(e) => onChange({ voiceoverMode: e.target.value })}
                >
                  <Radio.Button value='replace'>Replace original</Radio.Button>
                  <Tooltip title={clip.hasAudio === false ? "This recording has no original audio to mix with — use Replace instead." : ''}>
                    <Radio.Button value='overlay' disabled={clip.hasAudio === false}>Mix with original</Radio.Button>
                  </Tooltip>
                </Radio.Group>
                <div style={{ width: '100%' }}>
                  <Text type='secondary' style={{ fontSize: 12 }}>Voiceover volume — {Math.round((clip.voiceoverVolume ?? 1) * 100)}%</Text>
                  <Slider min={0} max={2} step={0.05} value={clip.voiceoverVolume ?? 1} onChange={(v) => onChange({ voiceoverVolume: v })} />
                </div>
                <Button
                  size='small'
                  danger
                  onClick={() => onChange({ voiceoverBlob: null, voiceoverUrl: null, voiceoverMode: null })}
                >
                  Remove Voiceover
                </Button>
              </Space>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 20, borderTop: '1px solid #E4E7EC', paddingTop: 14 }}>
        <Space align='center' style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: 13 }}>Captions</Text>
          {captions.length > 0 && <Text type='secondary' style={{ fontSize: 12 }}>({captions.length} line{captions.length > 1 ? 's' : ''})</Text>}
        </Space>

        {captions.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type='secondary' style={{ fontSize: 12 }}>
                No captions yet. Turn on "Auto-generate captions" before recording, or add lines by hand.
              </Text>
            }
            style={{ margin: '8px 0' }}
          />
        ) : (
          <div style={{ maxHeight: 220, overflowY: 'auto', paddingRight: 4 }}>
            {captions.map((c) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Button
                  size='small'
                  type='text'
                  style={{ fontSize: 11, color: '#667085', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                  onClick={() => seekTo(c.start)}
                  title='Jump to this line'
                >
                  {formatTime(c.start)}–{formatTime(c.end)}
                </Button>
                <Input
                  size='small'
                  value={c.text}
                  placeholder='Caption text'
                  onChange={(e) => updateCaption(c.id, { text: e.target.value })}
                  style={{ flex: 1 }}
                />
                <Button size='small' type='text' danger icon={<DeleteOutlined />} onClick={() => deleteCaption(c.id)} />
              </div>
            ))}
          </div>
        )}

        <Button size='small' icon={<PlusOutlined />} onClick={addCaption} style={{ marginTop: 4 }}>
          Add Caption Line
        </Button>
      </div>

      <div style={{ marginTop: 12 }}>
        <Text type='secondary' style={{ fontSize: 12 }}>Clip name</Text>
        <Input
          size='small'
          value={clip.name}
          maxLength={80}
          onChange={(e) => onChange({ name: e.target.value })}
          style={{ marginTop: 4, maxWidth: 320 }}
        />
      </div>
    </Card>
  )
}

export default ClipEditor
